/**
 * MIA (Medical Insider Analytics)
 * GA4-compatible analytics for healthcare and medical platforms
 * Professional analytics solution for medical job portals, career platforms, and healthcare websites
 */

class MedicalInsiderAnalytics {
    constructor(config = {}) {
        this.config = {
            endpoint: config.endpoint || '/api/analytics',
            trackingId: config.trackingId || 'MIA-TRACKER',
            debug: config.debug || false,
            
            // Healthcare careers specific configuration
            careersMode: config.careersMode !== false, // default true
            trackJobJourney: config.trackJobJourney !== false, // default true
            trackApplicationProcess: config.trackApplicationProcess !== false, // default true
            trackContentEngagement: config.trackContentEngagement !== false, // default true
            
            // DataLayer configuration
            dataLayerName: config.dataLayerName || 'dataLayer',
            createDataLayer: config.createDataLayer !== false, // default true
            
            // Detection and relay settings
            autoDetectGA: config.autoDetectGA !== false, // default true
            forwardAllEvents: config.forwardAllEvents !== false, // default true
            
            // GA4 format settings
            useGA4Format: config.useGA4Format !== false, // default true
            addCareersEnrichment: config.addCareersEnrichment !== false, // default true
            
            // Cookie consent configuration
            respectCookieConsent: config.respectCookieConsent !== false, // default true
            consentRequired: config.consentRequired || ['analytics'], // Required consent categories
            waitForConsent: config.waitForConsent !== false, // default true - wait for consent before tracking
            consentTimeout: config.consentTimeout || 10000, // 10 seconds timeout for consent
            
            ...config
        };

        // State tracking
        this.clientId = null;
        this.sessionId = null;
        this.userId = null;
        this.candidateId = null; // Job seeker ID
        this.employerId = null;  // Employer/recruiter ID
        this.sessionStartTime = null;
        this.lastActivityTime = null;
        this.isInitialized = false;
        
        // User context tracking
        this.userType = null; // 'student', 'resident', 'doctor', 'nurse', 'specialist', 'recruiter', 'employer'
        this.userSpecialty = null; // Medical specialty if applicable
        this.experienceLevel = null; // 'entry', 'junior', 'mid', 'senior', 'expert'
        
        // Job journey tracking
        this.currentJobApplication = null; // Active application being tracked
        this.jobSearchSession = null; // Current search session
        
        // GA detection state
        this.gaDetected = false;
        this.gtagDetected = false;
        this.gtmDetected = false;
        
        // DataLayer management
        this.dataLayer = null;
        this.originalDataLayerPush = null;
        this.eventQueue = [];
        
        // Automatic data collection
        this.automaticData = {};
        this.trafficSource = {};
        this.isFirstVisit = false;
        this.pageLoadTime = null;
        
        // Cookie consent state
        this.consentStatus = {};
        this.consentChecked = false;
        this.trackingAllowed = false;
        this.consentTimeout = null;

        this.init();
    }

    init() {
        this.log('Initializing MIA (Medical Insider Analytics)...');
        
        // Step 1: Detect existing GA setup
        this.detectGoogleAnalytics();
        
        // Step 2: Initialize or hijack dataLayer
        this.initializeDataLayer();
        
        // Step 3: Generate client/session IDs
        this.generateClientId();
        this.initializeSession();
        
        // Step 4: Collect automatic browser/environment data
        this.collectAutomaticData();
        
        // Step 5: Set up dataLayer interception
        this.interceptDataLayerEvents();
        
        // Step 6: Set up consent monitoring
        this.setupConsentMonitoring();
        
        // Step 7: Set up automatic page tracking
        this.setupAutomaticPageTracking();
        
        // Step 8: Add careers-specific tracking
        this.setupCareersTracking();
        
        this.isInitialized = true;
        
        // Step 9: Process any queued events
        this.processEventQueue();
        
        // Step 10: Send initial page view (if consent allows)
        if (this.isTrackingAllowed()) {
            this.sendAutomaticPageView();
        }
        
        this.log('MIA Analytics initialized', {
            gaDetected: this.gaDetected,
            careersMode: this.config.careersMode,
            mode: this.gaDetected ? 'relay' : 'standalone'
        });
    }

    detectGoogleAnalytics() {
        // Detect Google Analytics 4
        this.gtagDetected = typeof window.gtag === 'function';
        
        // Detect Google Tag Manager
        this.gtmDetected = !!(window.google_tag_manager || window.dataLayer);
        
        // Detect GA script presence
        const gaScripts = document.querySelectorAll('script[src*="googletagmanager.com/gtag/js"]');
        const gtmScripts = document.querySelectorAll('script[src*="googletagmanager.com/gtm.js"]');
        
        this.gaDetected = this.gtagDetected || this.gtmDetected || gaScripts.length > 0 || gtmScripts.length > 0;
        
        this.log('Google Analytics Detection:', {
            gaDetected: this.gaDetected,
            gtagDetected: this.gtagDetected,
            gtmDetected: this.gtmDetected,
            gaScripts: gaScripts.length,
            gtmScripts: gtmScripts.length
        });
    }

    initializeDataLayer() {
        const dataLayerName = this.config.dataLayerName;
        
        if (!window[dataLayerName]) {
            window[dataLayerName] = [];
            this.log(`Created ${dataLayerName}`);
        }
        
        this.dataLayer = window[dataLayerName];
        
        // Push initial careers context if enabled
        if (this.config.careersMode) {
            this.dataLayer.push({
                'careers_analytics_loaded': true,
                'careers_mode': true,
                'platform_type': 'healthcare_jobs',
                'ga_detected': this.gaDetected
            });
        }
    }

    interceptDataLayerEvents() {
        if (!this.dataLayer) return;
        
        // Store original push function
        this.originalDataLayerPush = this.dataLayer.push;
        
        // Override push function to intercept events
        const self = this;
        this.dataLayer.push = function(...args) {
            // Call original push first to maintain GA functionality
            const result = self.originalDataLayerPush.apply(this, args);
            
            // Process each argument for our relay
            args.forEach(item => {
                if (typeof item === 'object' && item !== null) {
                    self.processDataLayerEvent(item);
                }
            });
            
            return result;
        };
        
        this.log('DataLayer interception enabled');
    }

    processDataLayerEvent(event) {
        this.log('Processing dataLayer event:', event);
        
        // Skip our own events to avoid loops
        if (event.careers_analytics_loaded || event.source === 'careers_relay') {
            return;
        }
        
        // Convert to GA4 format and send to our API
        const ga4Event = this.convertToGA4Format(event);
        
        if (ga4Event) {
            this.sendToCustomAPI(ga4Event);
        }
    }

    convertToGA4Format(dataLayerEvent) {
        const timestamp = Date.now();
        
        // Base GA4 structure
        const ga4Event = {
            client_id: this.clientId,
            timestamp_micros: timestamp * 1000, // GA4 expects microseconds as number
        };
        
        // Add user ID if available
        if (this.userId) {
            ga4Event.user_id = this.userId;
        }
        
        // Convert dataLayer event to GA4 event format
        let eventName = 'custom_event';
        let eventParams = { ...dataLayerEvent };
        
        // Handle common GA4 event types
        if (dataLayerEvent.event) {
            eventName = dataLayerEvent.event;
            delete eventParams.event;
        }
        
        // Handle page_view events specially
        if (eventName === 'page_view' || eventName === 'gtm.dom' || eventName === 'gtm.load') {
            eventName = 'page_view';
            eventParams = {
                page_title: dataLayerEvent.page_title || document.title,
                page_location: dataLayerEvent.page_location || window.location.href,
                page_referrer: dataLayerEvent.page_referrer || document.referrer,
                ...eventParams
            };
        }
        
        // Add automatic data to all events
        eventParams = this.addAutomaticDataToEvent(eventParams);
        
        // Add careers enrichment if enabled
        if (this.config.addCareersEnrichment && this.config.careersMode) {
            eventParams = {
                ...eventParams,
                careers_session_id: this.sessionId,
                careers_client_id: this.clientId,
                user_type: this.userType,
                user_specialty: this.userSpecialty,
                experience_level: this.experienceLevel,
                platform_type: 'healthcare_jobs',
                ga_relay_mode: this.gaDetected
            };
            
            // Add candidate/employer context if available
            if (this.candidateId) {
                eventParams.candidate_id = this.candidateId;
                eventParams.user_context = 'job_seeker';
            }
            
            if (this.employerId) {
                eventParams.employer_id = this.employerId;
                eventParams.user_context = 'employer';
            }
            
            // Add current application context if tracking job journey
            if (this.currentJobApplication) {
                eventParams.application_id = this.currentJobApplication.id;
                eventParams.job_id = this.currentJobApplication.jobId;
                eventParams.job_title = this.currentJobApplication.jobTitle;
                eventParams.job_location = this.currentJobApplication.location;
                eventParams.job_specialty = this.currentJobApplication.specialty;
                eventParams.application_stage = this.currentJobApplication.stage;
                eventParams.application_method = this.currentJobApplication.method;
            }
            
            // Add search context if in job search session
            if (this.jobSearchSession) {
                eventParams.search_session_id = this.jobSearchSession.id;
                eventParams.search_keywords = this.jobSearchSession.keywords;
                eventParams.search_location = this.jobSearchSession.location;
                eventParams.search_specialty = this.jobSearchSession.specialty;
                eventParams.search_experience_level = this.jobSearchSession.experienceLevel;
            }
        }
        
        // Create GA4 events array
        ga4Event.events = [{
            name: eventName,
            params: this.sanitizeParams(eventParams)
        }];
        
        // Add user properties for careers context
        if (this.config.addCareersEnrichment && this.config.careersMode) {
            ga4Event.user_properties = {
                platform_type: { value: 'healthcare_jobs' },
                session_start_time: { value: this.sessionStartTime },
                user_type: { value: this.userType || 'unknown' },
                user_specialty: { value: this.userSpecialty || 'general' },
                experience_level: { value: this.experienceLevel || 'unknown' }
            };
            
            if (this.candidateId || this.employerId) {
                ga4Event.user_properties.user_context = { 
                    value: this.candidateId ? 'job_seeker' : 'employer' 
                };
            }
        }
        
        return ga4Event;
    }

    sanitizeParams(params) {
        const sanitized = {};
        
        // GA4 parameter name restrictions and type handling
        for (const [key, value] of Object.entries(params)) {
            // Clean parameter names (GA4 requirements)
            let cleanKey = key
                .toLowerCase()
                .replace(/[^a-z0-9_]/g, '_')
                .substring(0, 40); // GA4 limit
            
            // Handle value types and convert to models.Value format
            if (value !== null && value !== undefined) {
                if (typeof value === 'string') {
                    // Truncate long strings and wrap in models.Value format
                    const stringValue = value.length > 100 ? value.substring(0, 100) : value;
                    sanitized[cleanKey] = { string_value: stringValue };
                } else if (typeof value === 'number') {
                    if (Number.isInteger(value)) {
                        sanitized[cleanKey] = { int_value: value };
                    } else {
                        sanitized[cleanKey] = { float_value: value };
                    }
                } else if (typeof value === 'boolean') {
                    // Convert boolean to string for consistency
                    sanitized[cleanKey] = { string_value: value.toString() };
                } else if (typeof value === 'object') {
                    // Convert objects to JSON strings
                    sanitized[cleanKey] = { string_value: JSON.stringify(value) };
                } else {
                    // Fallback: convert to string
                    sanitized[cleanKey] = { string_value: value.toString() };
                }
            }
        }
        
        return sanitized;
    }

    generateClientId() {
        // Try to get existing GA client ID first
        this.clientId = this.getGAClientId() || this.getCookie('_mia_client_id');
        
        if (!this.clientId) {
            // Generate GA4-compatible client ID
            this.clientId = 'MIA1.1.' + Math.random().toString(36).substring(2, 15) + '.' + Math.floor(Date.now() / 1000);
            this.setCookie('_mia_client_id', this.clientId, 365 * 24 * 60 * 60 * 1000);
        }
    }

    getGAClientId() {
        // Try to extract from existing GA cookie
        const gaCookie = this.getCookie('_ga');
        if (gaCookie) {
            const parts = gaCookie.split('.');
            if (parts.length >= 4) {
                return `MIA1.1.${parts[2]}.${parts[3]}`;
            }
        }
        return null;
    }

    initializeSession() {
        const now = Date.now();
        const sessionTimeout = 30 * 60 * 1000; // 30 minutes
        
        const lastActivity = this.getCookie('_mia_last_activity');
        const sessionId = this.getCookie('_mia_session_id');
        
        if (sessionId && lastActivity && (now - parseInt(lastActivity)) < sessionTimeout) {
            this.sessionId = sessionId;
            this.sessionStartTime = parseInt(this.getCookie('_mia_session_start')) || now;
        } else {
            this.sessionId = 'mia_' + now + '_' + Math.random().toString(36).substring(2, 9);
            this.sessionStartTime = now;
            this.setCookie('_mia_session_id', this.sessionId, sessionTimeout);
            this.setCookie('_mia_session_start', this.sessionStartTime.toString(), sessionTimeout);
        }
        
        this.lastActivityTime = now;
        this.setCookie('_mia_last_activity', now.toString(), sessionTimeout);
    }

    setupCareersTracking() {
        if (!this.config.careersMode) return;
        
        // Track job search interactions
        this.setupJobSearchTracking();
        
        // Track application process
        this.setupApplicationTracking();
        
        // Track content engagement
        this.setupContentTracking();
        
        // Track page visibility for engagement metrics
        document.addEventListener('visibilitychange', () => {
            const event = {
                event: 'page_visibility_change',
                visibility_state: document.hidden ? 'hidden' : 'visible',
                source: 'careers_relay'
            };
            
            if (this.dataLayer) {
                this.dataLayer.push(event);
            }
        });
    }

    setupJobSearchTracking() {
        // Track job search forms
        document.addEventListener('submit', (e) => {
            const form = e.target;
            if (form.classList.contains('job-search-form') || form.id === 'job-search') {
                const formData = new FormData(form);
                
                const searchData = {
                    event: 'job_search',
                    search_keywords: formData.get('keywords') || formData.get('q'),
                    search_location: formData.get('location'),
                    search_specialty: formData.get('specialty'),
                    search_experience_level: formData.get('experience'),
                    search_job_type: formData.get('job_type'), // full-time, part-time, contract
                    source: 'careers_relay'
                };
                
                // Start new search session
                this.startJobSearchSession(searchData);
                
                if (this.dataLayer) {
                    this.dataLayer.push(searchData);
                }
            }
        });

        // Track job listing clicks
        document.addEventListener('click', (e) => {
            const jobCard = e.target.closest('.job-card, .job-listing, [data-job-id]');
            if (jobCard) {
                const jobId = jobCard.getAttribute('data-job-id') || jobCard.id;
                const jobTitle = jobCard.querySelector('.job-title, h2, h3')?.textContent;
                const employer = jobCard.querySelector('.employer, .company')?.textContent;
                const location = jobCard.querySelector('.location')?.textContent;
                const specialty = jobCard.querySelector('.specialty, .department')?.textContent;
                
                const event = {
                    event: 'job_listing_click',
                    job_id: jobId,
                    job_title: jobTitle,
                    employer: employer,
                    job_location: location,
                    job_specialty: specialty,
                    click_position: this.getJobCardPosition(jobCard),
                    source: 'careers_relay'
                };
                
                if (this.dataLayer) {
                    this.dataLayer.push(event);
                }
            }
        });
    }

    setupApplicationTracking() {
        // Track application start
        document.addEventListener('click', (e) => {
            const applyButton = e.target.closest('.apply-button, [data-action="apply"]');
            if (applyButton) {
                const jobId = applyButton.getAttribute('data-job-id') || 
                             document.querySelector('[data-job-id]')?.getAttribute('data-job-id');
                
                this.startJobApplication({
                    jobId: jobId,
                    jobTitle: document.querySelector('.job-title, h1')?.textContent,
                    location: document.querySelector('.job-location')?.textContent,
                    specialty: document.querySelector('.job-specialty')?.textContent,
                    stage: 'started',
                    method: applyButton.textContent.includes('External') ? 'external' : 'internal'
                });
            }
        });

        // Track application form submissions
        document.addEventListener('submit', (e) => {
            const form = e.target;
            if (form.classList.contains('application-form') || form.id === 'job-application') {
                const event = {
                    event: 'job_application_submit',
                    form_id: form.id,
                    application_method: 'online_form',
                    has_resume: !!form.querySelector('input[type="file"][name*="resume"]')?.files?.length,
                    has_cover_letter: !!form.querySelector('input[type="file"][name*="cover"]')?.files?.length,
                    source: 'careers_relay'
                };
                
                if (this.currentJobApplication) {
                    this.updateJobApplicationStage('submitted');
                    event.application_id = this.currentJobApplication.id;
                    event.job_id = this.currentJobApplication.jobId;
                }
                
                if (this.dataLayer) {
                    this.dataLayer.push(event);
                }
            }
        });
    }

    setupContentTracking() {
        // Track article/content engagement
        document.addEventListener('click', (e) => {
            const contentLink = e.target.closest('.article-link, .news-item, [data-content-type]');
            if (contentLink) {
                const contentType = contentLink.getAttribute('data-content-type') || 'article';
                const contentTitle = contentLink.querySelector('.title, h2, h3')?.textContent;
                const contentCategory = contentLink.getAttribute('data-category');
                
                const event = {
                    event: 'content_click',
                    content_type: contentType, // article, news, career_guide, salary_info
                    content_title: contentTitle,
                    content_category: contentCategory, // medical_news, career_advice, industry_trends
                    source: 'careers_relay'
                };
                
                if (this.dataLayer) {
                    this.dataLayer.push(event);
                }
            }
        });

        // Track newsletter/alert signups
        document.addEventListener('submit', (e) => {
            const form = e.target;
            if (form.classList.contains('newsletter-form') || form.classList.contains('job-alert-form')) {
                const formData = new FormData(form);
                
                const event = {
                    event: 'newsletter_signup',
                    signup_type: form.classList.contains('job-alert-form') ? 'job_alerts' : 'newsletter',
                    email: formData.get('email') ? 'provided' : 'not_provided',
                    specialty_interests: formData.get('specialty') || formData.get('interests'),
                    source: 'careers_relay'
                };
                
                if (this.dataLayer) {
                    this.dataLayer.push(event);
                }
            }
        });
    }

    // Healthcare Careers Specific Methods

    setUserType(userType, specialty = null, experienceLevel = null) {
        this.userType = userType; // 'student', 'resident', 'doctor', 'nurse', 'specialist', 'recruiter', 'employer'
        this.userSpecialty = specialty; // 'cardiology', 'emergency', 'pediatrics', etc.
        this.experienceLevel = experienceLevel; // 'entry', 'junior', 'mid', 'senior', 'expert'
        
        this.setCookie('_mia_user_type', userType, 365 * 24 * 60 * 60 * 1000);
        if (specialty) this.setCookie('_mia_user_specialty', specialty, 365 * 24 * 60 * 60 * 1000);
        if (experienceLevel) this.setCookie('_mia_experience_level', experienceLevel, 365 * 24 * 60 * 60 * 1000);
        
        // Push to dataLayer for immediate effect
        if (this.dataLayer) {
            this.dataLayer.push({
                event: 'user_profile_update',
                user_type: userType,
                user_specialty: specialty,
                experience_level: experienceLevel,
                source: 'mia_tracker'
            });
        }
    }

    setCandidateId(candidateId) {
        this.candidateId = candidateId;
        this.setCookie('_mia_candidate_id', candidateId, 365 * 24 * 60 * 60 * 1000);
        
        if (this.dataLayer) {
            this.dataLayer.push({
                event: 'candidate_identification',
                candidate_id: candidateId,
                source: 'mia_tracker'
            });
        }
    }

    setEmployerId(employerId) {
        this.employerId = employerId;
        this.setCookie('_mia_employer_id', employerId, 365 * 24 * 60 * 60 * 1000);
        
        if (this.dataLayer) {
            this.dataLayer.push({
                event: 'employer_identification',
                employer_id: employerId,
                source: 'mia_tracker'
            });
        }
    }

    startJobSearchSession(searchCriteria) {
        this.jobSearchSession = {
            id: 'search_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9),
            startTime: Date.now(),
            keywords: searchCriteria.search_keywords,
            location: searchCriteria.search_location,
            specialty: searchCriteria.search_specialty,
            experienceLevel: searchCriteria.search_experience_level,
            jobType: searchCriteria.search_job_type
        };
        
        this.log('Started job search session:', this.jobSearchSession);
    }

    startJobApplication(applicationData) {
        this.currentJobApplication = {
            id: 'app_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9),
            startTime: Date.now(),
            jobId: applicationData.jobId,
            jobTitle: applicationData.jobTitle,
            location: applicationData.location,
            specialty: applicationData.specialty,
            stage: applicationData.stage || 'started',
            method: applicationData.method || 'internal'
        };
        
        // Push application start event
        if (this.dataLayer) {
            this.dataLayer.push({
                event: 'job_application_start',
                application_id: this.currentJobApplication.id,
                job_id: this.currentJobApplication.jobId,
                job_title: this.currentJobApplication.jobTitle,
                application_method: this.currentJobApplication.method,
                source: 'careers_relay'
            });
        }
        
        this.log('Started job application:', this.currentJobApplication);
    }

    updateJobApplicationStage(stage) {
        if (this.currentJobApplication) {
            const previousStage = this.currentJobApplication.stage;
            this.currentJobApplication.stage = stage;
            
            if (this.dataLayer) {
                this.dataLayer.push({
                    event: 'job_application_stage_change',
                    application_id: this.currentJobApplication.id,
                    job_id: this.currentJobApplication.jobId,
                    previous_stage: previousStage,
                    new_stage: stage,
                    source: 'careers_relay'
                });
            }
        }
    }

    trackJobApplicationResult(result, details = {}) {
        if (this.currentJobApplication) {
            const event = {
                event: 'job_application_result',
                application_id: this.currentJobApplication.id,
                job_id: this.currentJobApplication.jobId,
                result: result, // 'submitted', 'interview_scheduled', 'rejected', 'hired'
                time_to_result: Date.now() - this.currentJobApplication.startTime,
                ...details,
                source: 'careers_relay'
            };
            
            if (this.dataLayer) {
                this.dataLayer.push(event);
            }
            
            // Clear current application if final result
            if (['rejected', 'hired', 'withdrawn'].includes(result)) {
                this.currentJobApplication = null;
            }
        }
    }

    getJobCardPosition(jobCard) {
        const jobCards = document.querySelectorAll('.job-card, .job-listing, [data-job-id]');
        return Array.from(jobCards).indexOf(jobCard) + 1;
    }

    // Standard methods
    setUserId(userId) {
        this.userId = userId;
        this.setCookie('_mia_user_id', userId, 365 * 24 * 60 * 60 * 1000);
        
        if (this.dataLayer) {
            this.dataLayer.push({
                event: 'user_identification',
                user_id: userId,
                source: 'mia_tracker'
            });
        }
    }

    trackEvent(eventName, parameters = {}) {
        const event = {
            event: eventName,
            ...parameters,
            source: 'mia_tracker'
        };
        
        if (this.dataLayer) {
            this.dataLayer.push(event);
        } else {
            const ga4Event = this.convertToGA4Format(event);
            this.sendToCustomAPI(ga4Event);
        }
    }

    sendToCustomAPI(ga4Event) {
        const payload = {
            ...ga4Event,
            sent_at: new Date().toISOString(),
            relay_version: '1.0.0',
            source: 'mia_analytics'
        };
        
        this.log('Sending GA4-formatted event to custom API:', payload);
        
        this.lastActivityTime = Date.now();
        this.setCookie('_mia_last_activity', this.lastActivityTime.toString(), 30 * 60 * 1000);
        
        this.sendViaFetch(payload);
    }

    sendViaFetch(payload) {
        fetch(this.config.endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
            keepalive: true
        }).catch(error => {
            this.log('Fetch failed:', error);
        });
    }

    processEventQueue() {
        while (this.eventQueue.length > 0) {
            const event = this.eventQueue.shift();
            this.processDataLayerEvent(event);
        }
    }

    // Utility methods
    getCookie(name) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
    }

    setCookie(name, value, maxAge) {
        const expires = new Date(Date.now() + maxAge).toUTCString();
        document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
    }

    log(...args) {
        if (this.config.debug) {
            console.log('[MIA Analytics]', ...args);
        }
    }

    // NEW METHODS: Automatic Data Collection (GA4-like functionality)

    collectAutomaticData() {
        this.log('Collecting automatic browser/environment data...');
        
        // Collect browser and device information
        this.automaticData = {
            // Screen information
            screen_resolution: `${screen.width}x${screen.height}`,
            screen_available_size: `${screen.availWidth}x${screen.availHeight}`,
            screen_color_depth: screen.colorDepth,
            
            // Viewport information
            viewport_size: `${window.innerWidth}x${window.innerHeight}`,
            
            // Browser information
            user_agent: navigator.userAgent,
            language: navigator.language,
            languages: navigator.languages ? navigator.languages.join(',') : navigator.language,
            
            // Platform information
            platform: navigator.platform,
            cookie_enabled: navigator.cookieEnabled,
            java_enabled: typeof navigator.javaEnabled === 'function' ? navigator.javaEnabled() : false,
            
            // Timezone information
            timezone: this.getTimezone(),
            timezone_offset: new Date().getTimezoneOffset(),
            
            // Connection information (if available)
            connection_type: this.getConnectionType(),
            
            // Performance information
            page_load_time: this.getPageLoadTime(),
            
            // Document information
            document_encoding: document.characterSet || document.charset,
            document_referrer: document.referrer,
            document_title: document.title,
            document_location: window.location.href,
            
            // Additional browser capabilities
            local_storage_enabled: this.isLocalStorageEnabled(),
            session_storage_enabled: this.isSessionStorageEnabled()
        };
        
        // Parse traffic source and UTM parameters
        this.trafficSource = this.parseTrafficSource();
        
        // Detect first visit
        this.isFirstVisit = this.detectFirstVisit();
        
        this.log('Automatic data collected:', {
            automaticData: this.automaticData,
            trafficSource: this.trafficSource,
            isFirstVisit: this.isFirstVisit
        });
    }

    getTimezone() {
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch (e) {
            return 'Unknown';
        }
    }

    getConnectionType() {
        if (navigator.connection) {
            return navigator.connection.effectiveType || navigator.connection.type || 'unknown';
        }
        return 'unknown';
    }

    getPageLoadTime() {
        if (window.performance && window.performance.timing) {
            const timing = window.performance.timing;
            const loadTime = timing.loadEventEnd - timing.navigationStart;
            return loadTime > 0 ? loadTime : null;
        }
        return null;
    }

    isLocalStorageEnabled() {
        try {
            const test = 'test';
            localStorage.setItem(test, test);
            localStorage.removeItem(test);
            return true;
        } catch (e) {
            return false;
        }
    }

    isSessionStorageEnabled() {
        try {
            const test = 'test';
            sessionStorage.setItem(test, test);
            sessionStorage.removeItem(test);
            return true;
        } catch (e) {
            return false;
        }
    }

    parseTrafficSource() {
        const url = new URL(window.location.href);
        const referrer = document.referrer;
        
        const trafficSource = {
            // UTM parameters
            utm_source: url.searchParams.get('utm_source'),
            utm_medium: url.searchParams.get('utm_medium'),
            utm_campaign: url.searchParams.get('utm_campaign'),
            utm_term: url.searchParams.get('utm_term'),
            utm_content: url.searchParams.get('utm_content'),
            
            // Other common tracking parameters
            gclid: url.searchParams.get('gclid'), // Google Ads
            fbclid: url.searchParams.get('fbclid'), // Facebook
            msclkid: url.searchParams.get('msclkid'), // Microsoft Ads
            
            // Referrer information
            referrer: referrer,
            referrer_domain: this.extractDomain(referrer),
            
            // Derived source/medium
            source: null,
            medium: null,
            campaign: null
        };
        
        // Determine source and medium based on UTM or referrer
        if (trafficSource.utm_source && trafficSource.utm_medium) {
            trafficSource.source = trafficSource.utm_source;
            trafficSource.medium = trafficSource.utm_medium;
            trafficSource.campaign = trafficSource.utm_campaign;
        } else if (trafficSource.gclid) {
            trafficSource.source = 'google';
            trafficSource.medium = 'cpc';
        } else if (trafficSource.fbclid) {
            trafficSource.source = 'facebook';
            trafficSource.medium = 'cpc';
        } else if (referrer) {
            const referrerInfo = this.categorizeReferrer(referrer);
            trafficSource.source = referrerInfo.source;
            trafficSource.medium = referrerInfo.medium;
        } else {
            trafficSource.source = '(direct)';
            trafficSource.medium = '(none)';
        }
        
        return trafficSource;
    }

    extractDomain(url) {
        if (!url) return null;
        try {
            return new URL(url).hostname;
        } catch (e) {
            return null;
        }
    }

    categorizeReferrer(referrer) {
        if (!referrer) return { source: '(direct)', medium: '(none)' };
        
        const domain = this.extractDomain(referrer);
        if (!domain) return { source: '(direct)', medium: '(none)' };
        
        // Search engines
        const searchEngines = {
            'google.com': 'google',
            'google.co.uk': 'google',
            'google.ca': 'google',
            'bing.com': 'bing',
            'yahoo.com': 'yahoo',
            'duckduckgo.com': 'duckduckgo',
            'baidu.com': 'baidu'
        };
        
        // Social media platforms
        const socialPlatforms = {
            'facebook.com': 'facebook',
            'twitter.com': 'twitter',
            'linkedin.com': 'linkedin',
            'instagram.com': 'instagram',
            'youtube.com': 'youtube',
            'tiktok.com': 'tiktok',
            'pinterest.com': 'pinterest'
        };
        
        // Check for search engines
        for (const [searchDomain, searchName] of Object.entries(searchEngines)) {
            if (domain.includes(searchDomain)) {
                return { source: searchName, medium: 'organic' };
            }
        }
        
        // Check for social media
        for (const [socialDomain, socialName] of Object.entries(socialPlatforms)) {
            if (domain.includes(socialDomain)) {
                return { source: socialName, medium: 'social' };
            }
        }
        
        // Default to referral
        return { source: domain, medium: 'referral' };
    }

    detectFirstVisit() {
        const firstVisitCookie = this.getCookie('_mia_first_visit');
        if (!firstVisitCookie) {
            this.setCookie('_mia_first_visit', 'true', 365 * 24 * 60 * 60 * 1000);
            return true;
        }
        return false;
    }

    setupAutomaticPageTracking() {
        this.log('Setting up automatic page tracking...');
        
        // Track page visibility changes
        document.addEventListener('visibilitychange', () => {
            this.trackEvent('page_visibility_change', {
                visibility_state: document.hidden ? 'hidden' : 'visible',
                timestamp: Date.now()
            });
        });
        
        // Track page unload
        window.addEventListener('beforeunload', () => {
            this.trackEvent('page_unload', {
                time_on_page: Date.now() - this.sessionStartTime,
                timestamp: Date.now()
            });
        });
        
        // Track scroll depth
        this.setupScrollTracking();
        
        // Track clicks on external links
        this.setupExternalLinkTracking();
        
        // Monitor for SPA navigation (history API changes)
        this.setupSPATracking();
    }

    setupScrollTracking() {
        let maxScrollDepth = 0;
        let scrollDepthMarkers = [25, 50, 75, 90, 100];
        let firedMarkers = new Set();
        
        const trackScrollDepth = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            const scrollPercent = Math.round((scrollTop / docHeight) * 100);
            
            if (scrollPercent > maxScrollDepth) {
                maxScrollDepth = scrollPercent;
                
                // Fire events for scroll depth milestones
                scrollDepthMarkers.forEach(marker => {
                    if (scrollPercent >= marker && !firedMarkers.has(marker)) {
                        firedMarkers.add(marker);
                        this.trackEvent('scroll_depth', {
                            scroll_depth: marker,
                            page_location: window.location.href
                        });
                    }
                });
            }
        };
        
        // Throttle scroll events
        let scrollTimeout;
        window.addEventListener('scroll', () => {
            if (scrollTimeout) clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(trackScrollDepth, 100);
        });
    }

    setupExternalLinkTracking() {
        document.addEventListener('click', (e) => {
            const link = e.target.closest('a');
            if (link && link.href) {
                const linkDomain = this.extractDomain(link.href);
                const currentDomain = window.location.hostname;
                
                if (linkDomain && linkDomain !== currentDomain) {
                    this.trackEvent('external_link_click', {
                        link_url: link.href,
                        link_domain: linkDomain,
                        link_text: link.textContent?.trim() || '',
                        page_location: window.location.href
                    });
                }
            }
        });
    }

    setupSPATracking() {
        // Track history API changes for Single Page Applications
        const originalPushState = history.pushState;
        const originalReplaceState = history.replaceState;
        
        const trackPageChange = () => {
            // Small delay to ensure DOM has updated
            setTimeout(() => {
                this.sendAutomaticPageView();
            }, 100);
        };
        
        history.pushState = function(...args) {
            originalPushState.apply(history, args);
            trackPageChange();
        };
        
        history.replaceState = function(...args) {
            originalReplaceState.apply(history, args);
            trackPageChange();
        };
        
        // Also listen for popstate (back/forward buttons)
        window.addEventListener('popstate', trackPageChange);
    }

    sendAutomaticPageView() {
        if (!this.isInitialized) return;
        
        const pageViewData = {
            event: 'page_view',
            page_title: document.title,
            page_location: window.location.href,
            page_referrer: document.referrer,
            
            // Add all automatic data
            ...this.automaticData,
            
            // Add traffic source data
            ...this.trafficSource,
            
            // Add session information
            session_id: this.sessionId,
            session_start_time: this.sessionStartTime,
            is_first_visit: this.isFirstVisit,
            
            // Add timing information
            page_load_time: this.getPageLoadTime(),
            timestamp: Date.now(),
            
            source: 'mia_automatic'
        };
        
        this.log('Sending automatic page view:', pageViewData);
        
        // Send through dataLayer if available, otherwise directly to API
        if (this.dataLayer) {
            this.dataLayer.push(pageViewData);
        } else {
            const ga4Event = this.convertToGA4Format(pageViewData);
            this.sendToCustomAPI(ga4Event);
        }
    }

    // Enhanced convertToGA4Format to include automatic data
    addAutomaticDataToEvent(eventParams) {
        // Add automatic browser/device data to all events
        const enhancedParams = {
            ...eventParams,
            
            // Browser/Device data
            screen_resolution: this.automaticData.screen_resolution,
            viewport_size: this.automaticData.viewport_size,
            user_language: this.automaticData.language,
            user_agent: this.automaticData.user_agent,
            platform: this.automaticData.platform,
            timezone: this.automaticData.timezone,
            
            // Traffic source data
            traffic_source: this.trafficSource.source,
            traffic_medium: this.trafficSource.medium,
            traffic_campaign: this.trafficSource.campaign,
            
            // UTM parameters
            utm_source: this.trafficSource.utm_source,
            utm_medium: this.trafficSource.utm_medium,
            utm_campaign: this.trafficSource.utm_campaign,
            utm_term: this.trafficSource.utm_term,
            utm_content: this.trafficSource.utm_content,
            
            // Session data
            session_id: this.sessionId,
            is_first_visit: this.isFirstVisit,
            
            // Page data
            page_title: document.title,
            page_location: window.location.href,
            page_referrer: document.referrer
        };
        
        return enhancedParams;
    }

    // COOKIE CONSENT INTEGRATION METHODS

    checkConsentStatus(category = 'analytics') {
        // Check if vanilla-cookieconsent is available
        if (typeof window.CookieConsent !== 'undefined' && window.CookieConsent.acceptedCategory) {
            return window.CookieConsent.acceptedCategory(category);
        }
        
        // Fallback: check for consent cookie manually
        const consentCookie = this.getCookie('cc_cookie');
        if (consentCookie) {
            try {
                const consent = JSON.parse(consentCookie);
                return consent.categories && consent.categories[category] === true;
            } catch (e) {
                this.log('Error parsing consent cookie:', e);
            }
        }
        
        // If no consent system detected, allow tracking (backward compatibility)
        if (!this.config.respectCookieConsent) {
            return true;
        }
        
        // Default to false if consent is required but not found
        return false;
    }

    isTrackingAllowed() {
        if (!this.config.respectCookieConsent) {
            return true;
        }
        
        // Check all required consent categories
        for (const category of this.config.consentRequired) {
            if (!this.checkConsentStatus(category)) {
                return false;
            }
        }
        
        return true;
    }

    handleConsentChange(consentData) {
        this.log('Consent change detected:', consentData);
        
        const wasAllowed = this.trackingAllowed;
        this.trackingAllowed = this.isTrackingAllowed();
        
        if (!wasAllowed && this.trackingAllowed) {
            // Consent granted - start tracking
            this.log('Consent granted - enabling tracking');
            this.onConsentGranted();
        } else if (wasAllowed && !this.trackingAllowed) {
            // Consent revoked - stop tracking
            this.log('Consent revoked - disabling tracking');
            this.onConsentRevoked();
        }
        
        // Track the consent change event itself (this is usually allowed)
        this.trackConsentEvent(consentData);
    }

    onConsentGranted() {
        // Initialize tracking cookies if not already set
        if (!this.clientId) {
            this.generateClientId();
        }
        
        if (!this.sessionId) {
            this.initializeSession();
        }
        
        // Send queued events
        this.processEventQueue();
        
        // Send a page view if we haven't already
        if (this.isInitialized) {
            this.sendAutomaticPageView();
        }
    }

    onConsentRevoked() {
        // Clear tracking cookies
        this.clearTrackingCookies();
        
        // Clear event queue
        this.eventQueue = [];
        
        // Reset tracking state
        this.clientId = null;
        this.sessionId = null;
    }

    clearTrackingCookies() {
        const cookiesToClear = [
            '_mia_client_id',
            '_mia_session_id',
            '_mia_session_start',
            '_mia_last_activity',
            '_mia_first_visit',
            '_mia_user_type',
            '_mia_user_specialty',
            '_mia_experience_level',
            '_mia_candidate_id',
            '_mia_employer_id',
            '_mia_user_id'
        ];
        
        cookiesToClear.forEach(cookieName => {
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        });
        
        this.log('Tracking cookies cleared');
    }

    trackConsentEvent(consentData) {
        // Track consent events even if analytics consent is not given
        // This is typically allowed as it's necessary for compliance
        const consentEvent = {
            event: 'cookie_consent_update',
            consent_analytics: consentData.consent_analytics || false,
            consent_marketing: consentData.consent_marketing || false,
            consent_necessary: true, // Always true
            consent_timestamp: Date.now(),
            source: 'mia_consent'
        };
        
        // Send directly to API without consent check
        const ga4Event = this.convertToGA4Format(consentEvent);
        this.sendToCustomAPI(ga4Event);
    }

    setupConsentMonitoring() {
        // Monitor for consent changes from vanilla-cookieconsent
        if (typeof window.CookieConsent !== 'undefined') {
            // Listen for consent changes
            document.addEventListener('cc:onConsent', (e) => {
                this.handleConsentChange({
                    consent_analytics: this.checkConsentStatus('analytics'),
                    consent_marketing: this.checkConsentStatus('marketing')
                });
            });
            
            document.addEventListener('cc:onChange', (e) => {
                this.handleConsentChange({
                    consent_analytics: this.checkConsentStatus('analytics'),
                    consent_marketing: this.checkConsentStatus('marketing')
                });
            });
        }
        
        // Monitor dataLayer for consent events
        if (this.dataLayer) {
            // Check for existing consent events in dataLayer
            const checkForConsentEvents = () => {
                this.dataLayer.forEach(item => {
                    if (item.event === 'cookie_consent_analytics' || item.event === 'cookie_consent_marketing') {
                        this.handleConsentChange({
                            consent_analytics: item.consent_analytics,
                            consent_marketing: item.consent_marketing
                        });
                    }
                });
            };
            
            // Check immediately and periodically
            checkForConsentEvents();
            setInterval(checkForConsentEvents, 1000);
        }
        
        // Set initial tracking state
        this.trackingAllowed = this.isTrackingAllowed();
        this.log('Initial tracking allowed:', this.trackingAllowed);
    }

    // Override sendToCustomAPI to respect consent
    sendToCustomAPIWithConsent(ga4Event) {
        // Always allow consent-related events
        const eventName = ga4Event.events?.[0]?.name;
        const isConsentEvent = eventName === 'cookie_consent_update' || 
                              eventName === 'cookie_consent_analytics' || 
                              eventName === 'cookie_consent_marketing';
        
        if (!isConsentEvent && !this.isTrackingAllowed()) {
            this.log('Tracking blocked - consent not granted');
            // Queue the event for later if consent is granted
            this.eventQueue.push(ga4Event);
            return;
        }
        
        // Proceed with normal sending
        this.sendToCustomAPI(ga4Event);
    }

    // Override processDataLayerEvent to handle consent
    processDataLayerEventWithConsent(event) {
        this.log('Processing dataLayer event:', event);
        
        // Handle consent events specially
        if (event.event === 'cookie_consent_analytics' || event.event === 'cookie_consent_marketing') {
            this.handleConsentChange({
                consent_analytics: event.consent_analytics,
                consent_marketing: event.consent_marketing
            });
            return;
        }
        
        // Skip our own events to avoid loops
        if (event.careers_analytics_loaded || event.source === 'careers_relay') {
            return;
        }
        
        // Convert to GA4 format and send to our API (with consent check)
        const ga4Event = this.convertToGA4Format(event);
        
        if (ga4Event) {
            this.sendToCustomAPIWithConsent(ga4Event);
        }
    }
}

// Global interface for manual tracking - MIA Analytics
window.mia = {
    track: function(eventName, parameters) {
        if (window.medicalInsiderAnalytics) {
            window.medicalInsiderAnalytics.trackEvent(eventName, parameters);
        } else {
            console.warn('MIA (Medical Insider Analytics) not initialized');
        }
    },
    
    setUserId: function(userId) {
        if (window.medicalInsiderAnalytics) {
            window.medicalInsiderAnalytics.setUserId(userId);
        }
    },
    
    setUserType: function(userType, specialty, experienceLevel) {
        if (window.medicalInsiderAnalytics) {
            window.medicalInsiderAnalytics.setUserType(userType, specialty, experienceLevel);
        }
    },
    
    setCandidateId: function(candidateId) {
        if (window.medicalInsiderAnalytics) {
            window.medicalInsiderAnalytics.setCandidateId(candidateId);
        }
    },
    
    setEmployerId: function(employerId) {
        if (window.medicalInsiderAnalytics) {
            window.medicalInsiderAnalytics.setEmployerId(employerId);
        }
    },
    
    trackApplicationResult: function(result, details) {
        if (window.medicalInsiderAnalytics) {
            window.medicalInsiderAnalytics.trackJobApplicationResult(result, details);
        }
    }
};

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        if (window.miaConfig) {
            window.medicalInsiderAnalytics = new MedicalInsiderAnalytics(window.miaConfig);
        }
    });
} else {
    if (window.miaConfig) {
        window.medicalInsiderAnalytics = new MedicalInsiderAnalytics(window.miaConfig);
    }
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
    module.exports = MedicalInsiderAnalytics;
}
